import { h, Component, Fragment } from 'preact';
import { createPortal } from 'preact/compat';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import lockScreenStore from '../../stores/lockScreen';
import activityTracker from '../../lib/activityTracker';
import { captureScreenshot, getSupportReportContext } from '../../lib/captureScreenshot';
import { observeReportButtonPosition } from '../../lib/reportButtonPosition';
import NewTicket from './newTicket';
import './reportProblem.css';

@observer
class ReportProblem extends Component {
    state = { capturing: false, open: false, captureError: null, screenshot: null, ticket: null, busy: false };

    get canReport() {
        const { userStore, appState } = this.props.stores;
        return typeof window.HTMLDialogElement?.prototype.showModal === 'function'
            && !!userStore.user?.id && !userStore.isBlockedFromSupportTickets && !lockScreenStore.isLocked
            && !/^\/support\/new(?:[/?]|$)/.test(appState.path || '');
    }

    componentDidMount() {
        this.updatePositionObserver();
    }

    componentDidUpdate() {
        this.updatePositionObserver();
        if (!this.canReport && (this.state.open || this.state.capturing)) {
            this.resetReport();
        } else if (this.state.open && this.dialog && !this.dialog.open) {
            this.dialog.showModal();
        }
    }

    componentWillUnmount() {
        this.stopObservingPosition?.();
        this.captureController?.abort();
        this.releaseScreenshot();
    }

    // trigger='panel': the button is an ordinary full-width button inside the help panel
    // (the panel owns the fixed positioning), not the floating pill.
    get isPanelTrigger() {
        return this.props.trigger === 'panel';
    }

    updatePositionObserver() {
        const button = this.isPanelTrigger ? null : this.button;
        if (this.observedButton === button) return;
        this.stopObservingPosition?.();
        this.observedButton = button;
        this.stopObservingPosition = button ? observeReportButtonPosition(button) : null;
    }

    releaseScreenshot = () => {
        if (this.screenshotUrl) URL.revokeObjectURL(this.screenshotUrl);
        this.screenshotUrl = null;
    }

    report = async () => {
        if (!this.canReport || this.captureController || this.state.open) return;
        const controller = new AbortController();
        this.captureController = controller;
        const reportContext = getSupportReportContext(this.props.stores.appState);
        // Hide immediately without leaving the click stack required by getDisplayMedia.
        // The host (help panel) hides itself too so the screenshot shows the page, not the panel.
        this.props.onCaptureStart?.();
        if (this.button) this.button.style.visibility = 'hidden';
        this.setState({ capturing: true, ticket: null, captureError: null });
        let screenshot = null;
        let errorCode = null;
        try {
            screenshot = await captureScreenshot({ signal: controller.signal });
            if (controller.signal.aborted) return;
            this.screenshotUrl = URL.createObjectURL(screenshot);
        } catch (error) {
            if (controller.signal.aborted) return;
            errorCode = error.code === 'unsupported' ? 'unsupported'
                : error.name === 'NotAllowedError' ? 'cancelled' : 'failed';
        } finally {
            this.captureController = null;
            this.props.onCaptureEnd?.();
        }
        if (controller.signal.aborted) return;
        this.setState({ capturing: false, open: true, screenshot, captureError: errorCode, reportContext });
    }

    close = (event) => {
        event?.preventDefault();
        if (this.state.busy) return;
        this.resetReport(true);
    }

    dialogClosed = (event) => {
        const dialog = event.currentTarget;
        if (dialog !== this.dialog || dialog.open || !this.state.open || this.resettingReport) return;
        if (this.state.busy && this.canReport) {
            dialog.showModal();
        } else {
            this.resetReport(true);
        }
    }

    keyDown = (event) => {
        activityTracker.onActivity();
        event.stopPropagation();
    }

    resetReport = (restoreFocus = false) => {
        this.resettingReport = true;
        this.captureController?.abort();
        this.dialog?.close();
        this.releaseScreenshot();
        this.setState({ open: false, capturing: false, screenshot: null, ticket: null, busy: false }, () => {
            this.resettingReport = false;
            if (restoreFocus) this.button?.focus();
        });
    }

    submitted = (ticket) => {
        this.releaseScreenshot();
        this.setState({ ticket, screenshot: null });
    }

    render() {
        if (!this.canReport) return null;
        const { capturing, open, screenshot, captureError, reportContext, ticket, busy } = this.state;
        const buttonClass = this.isPanelTrigger
            ? 'btn btn-sm btn-outline-secondary rounded-pill w-100'
            : 'badge text-bg-primary rounded-pill border-0 px-2 py-1 report-problem-button';
        return <>
            <button
                type='button'
                class={buttonClass}
                onClick={this.report}
                style={{ visibility: capturing ? 'hidden' : 'visible' }}
                disabled={capturing || open}
                ref={element => { this.button = element; }}
            >
                <i class={`fa-solid ${capturing ? 'fa-spinner fa-spin' : 'fa-camera'} me-1`} />
                <Text id='support.reportProblem.button'>Report a problem</Text>
            </button>
            {open && createPortal(
                <dialog
                    class='report-problem-dialog bg-body text-body border rounded-3 shadow-lg p-0'
                    aria-labelledby='report-problem-title'
                    ref={element => { this.dialog = element; }}
                    onCancel={this.close}
                    onClose={this.dialogClosed}
                    onKeyDown={this.keyDown}
                    onKeyUp={event => event.stopPropagation()}
                    onKeyPress={event => event.stopPropagation()}
                >
                    <div class='d-flex align-items-center justify-content-between border-bottom p-3'>
                        <h2 class='h5 mb-0' id='report-problem-title'>
                            <Text id='support.reportProblem.button'>Report a problem</Text>
                        </h2>
                        <button type='button' class='btn btn-sm btn-outline-secondary' onClick={this.close}
                            disabled={busy}>
                            <Text id='support.reportProblem.close'>Close</Text>
                        </button>
                    </div>
                    {ticket ? (
                        <div class='p-4' role='status'>
                            <p><Text id='support.reportProblem.sent'>Your support ticket has been sent.</Text></p>
                            <p class='fw-bold'>{ticket.ticketNumber}</p>
                            <button type='button' class='btn btn-primary rounded-pill' onClick={this.close}>
                                <Text id='support.reportProblem.close'>Close</Text>
                            </button>
                        </div>
                    ) : <>
                        {captureError && <div class='alert alert-warning m-3' role='status'>
                            {captureError === 'unsupported' ? (
                                <Text id='support.reportProblem.unsupported'>
                                    This browser cannot capture the screen. You can attach or paste a screenshot below.
                                </Text>
                            ) : captureError === 'cancelled' ? (
                                <Text id='support.reportProblem.cancelled'>
                                    Screen capture was cancelled or denied. You can attach or paste a screenshot below.
                                </Text>
                            ) : (
                                <Text id='support.reportProblem.failed'>
                                    Screen capture failed. You can attach or paste a screenshot below.
                                </Text>
                            )}
                        </div>}
                        <NewTicket stores={this.props.stores} reportContext={reportContext}
                            screenshot={screenshot} screenshotUrl={this.screenshotUrl}
                            onCancel={this.close} onSubmitted={this.submitted}
                            onBusyChange={value => this.setState({ busy: value })} />
                    </>}
                </dialog>, document.body
            )}
        </>;
    }
}

export default ReportProblem;
